import { type PostFactiii } from '@shared/all/domains/factiiis';
import { ModerationReportType } from '@shared/all/domains/moderation';
import { type Post, type PostResponse, VoteType } from '@shared/all/domains/posts';
import { type Upload } from '@shared/all/domains/uploads';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { copyToClipboard } from '../helpers/clipboard';
import { SITE_URL } from '../helpers/config';
import { useRouteName } from '../helpers/navigation';
import toast from '../helpers/toast';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import { getDimensionsFromUri } from '../helpers/uploadHelper';
import useDatabase from './useDatabase';
import useFactiiis from './useFactiiis';
import useLocalPreference from './useLocalPreference';
import { postBodyQueryKey } from './usePostBody';
import usePreference from './usePreference';
import useUpload from './useUpload';
import useUser from './useUser';
export type ModalOptions = undefined | 'REPORT' | 'PAGE_OPTIONS' | 'REPOST' | 'EDIT' | 'HISTORY' | 'AWARD' | 'BAN_CONFIRMATION' | 'BAN_USER_CONFIRMATION' | 'DELETE_POST_CONFIRMATION' | 'EMBED' | 'TAG_USERS' | 'CROSS_POST';

/**
 * Shared usePosts hook for managing post operations across web and mobile.
 *
 * @param client - The tRPC client instance (required)
 * @param uniqueId - Optional unique identifier for distinguishing query keys
 */
const usePosts = (client: WrappedTRPCClient, uniqueId?: string) => {
  const queryClient = useQueryClient();
  const {
    localPreferenceState
  } = useLocalPreference();
  const {
    preferenceState
  } = usePreference(client);
  const routeName = useRouteName();
  const {
    currentUser
  } = useUser();
  const {
    getFactiiiId
  } = useFactiiis(client);
  const {
    uploadOne
  } = useUpload(client);
  const {
    createPrivatePost,
    deletePrivatePost,
    fetchPrivatePost,
    fetchPrivatePosts,
    editPrivatePost,
    fetchPrivateMyFactiiiTags,
    fetchPrivatePostReplies
  } = useDatabase({
    getFactiiiId
  });
  const [openedModal, setOpenedModal] = useState<ModalOptions>(undefined);
  const [selectedPostForBan, setSelectedPostForBan] = useState<Post | null>(null);
  const [linkCopiedStates, setLinkCopiedStates] = useState<Record<string, boolean>>({});

  // Query key construction - consistent across both platforms
  // React Compiler automatically memoizes this based on dependencies
  const queryKey = ['Posts', localPreferenceState.postFilterType, localPreferenceState.displayMode, preferenceState.allowNSFWContent, localPreferenceState.postModeType, localPreferenceState.selectedFactiiis, localPreferenceState.dataFilter, routeName, currentUser.username, uniqueId];

  // Recursively finds post id within nested post replies
  const findPostById = (posts: Post[], postId: string): Post | null => {
    for (const post of posts) {
      if (post.id === postId) {
        return post;
      }
      if (post.replies && post.replies.length > 0) {
        const foundPost = findPostById(post.replies, postId);
        if (foundPost) {
          return foundPost;
        }
      }
    }
    return null;
  };
  const appendReply = async (newReply: Post, getReplies = false) => {
    const addReplyToReplies = (replies: Post[], parentId: string): Post[] => {
      return replies.map(currentReply => {
        if (currentReply.id === parentId) {
          return {
            ...currentReply,
            replies_count: getReplies ? currentReply.replies_count : currentReply.replies_count + 1,
            replies: getReplies ? [...(currentReply.replies || []), newReply] : [newReply, ...(currentReply.replies || [])]
          };
        } else if (currentReply.replies) {
          return {
            ...currentReply,
            replies: addReplyToReplies(currentReply.replies, parentId)
          };
        } else {
          return currentReply;
        }
      });
    };
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData => {
      if (!oldData) return oldData;
      const updatedPages = oldData.pages.map(page => ({
        ...page,
        posts: page.posts.map(post_0 => post_0.id === newReply.parentPostId ? {
          ...post_0,
          replies_count: getReplies ? post_0.replies_count : post_0.replies_count + 1,
          replies: getReplies ? [...(post_0.replies || []), newReply] : [newReply, ...(post_0.replies || [])]
        } : {
          ...post_0,
          replies: post_0.replies ? addReplyToReplies(post_0.replies, newReply.parentPostId ?? '') : post_0.replies
        })
      }));
      return {
        ...oldData,
        pages: updatedPages
      };
    });
  };
  const fetchPostReplies = async (postId_0: string) => {
    const oldData_0 = queryClient.getQueryData<InfiniteData<PostResponse>>(queryKey);
    if (!oldData_0) return;
    const post_1 = findPostById(oldData_0.pages.flatMap(page_0 => page_0.posts), postId_0);
    if (!post_1) return;
    if (post_1.isPrivate) {
      // Calculate the page based on currently loaded replies (10 per page)
      const page_1 = Math.floor((post_1.replies?.length || 0) / 10);
      const {
        replies: replies_0
      } = await fetchPrivatePostReplies({
        postId: postId_0,
        page: page_1
      });
      replies_0.forEach(reply => {
        void appendReply(reply, true);
      });
    } else {
      const cursor = post_1.replies?.length ? post_1.replies[post_1.replies.length - 1].id : undefined;
      const [error, repliesData] = await client.posts.replies.query({
        postId: postId_0,
        cursor
      });
      if (error) return;
      repliesData.posts.forEach(reply_0 => {
        void appendReply(reply_0, true);
      });
    }
  };
  const hidePost = async (id: string) => {
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_1 => {
      if (!oldData_1 || oldData_1?.pages?.length === 0) return oldData_1;
      const newPages = oldData_1.pages.map(page_2 => ({
        ...page_2,
        posts: page_2.posts.filter(post_2 => post_2.id !== id)
      }));
      return {
        ...oldData_1,
        pages: newPages
      };
    });
  };

  // Archive a post or restore it. Either way it leaves the list on screen: an
  // archived post is out of every feed, and a restored one is out of the
  // "show archived" view. A failure is toasted by the tRPC client.
  const setArchivedHandler = async (postId_1: string, archived: boolean) => {
    const [error_0] = await client.posts.setArchived.mutate({
      postId: postId_1,
      archived
    });
    if (error_0) return;
    void hidePost(postId_1);
    toast(archived ? 'Post archived' : 'Post restored');
  };
  const postDataFactiii = async (postId_2: string, factiiiId: number, data: string) => {
    const addFactiii = {
      factiiiId,
      data,
      uploads: [],
      spaceTimes: [],
      anonymous: false
    };
    const [error_1] = await client.factiiis.tag.mutate({
      postId: postId_2,
      addFactiiis: [addFactiii],
      removeFactiiiIds: []
    });
    if (error_1) return error_1;
    void hidePost(postId_2);
  };

  // `deviceLocal` — NOT "is private". The local branch deletes from SQLite on
  // this device, so a server-stored private post (a synced vault file, or one
  // toggled private) must take the server branch or the delete silently finds
  // nothing and reports failure. See isServerPrivate in the post DTO.
  const deletePost = async (id_0: string, deviceLocal?: boolean) => {
    if (deviceLocal) {
      const success = await deletePrivatePost(id_0);
      if (!success) {
        toast('Failed to delete private post', '', 'error');
        return new Error('Failed to delete private post');
      }
    } else {
      const [error_2] = await client.posts.delete.mutate({
        postId: id_0
      });
      if (error_2) return error_2;
    }
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_2 => {
      if (!oldData_2) return oldData_2;
      const newPages_0 = oldData_2.pages.map(page_3 => ({
        ...page_3,
        posts: page_3.posts.filter(post_3 => post_3.id !== id_0)
      }));
      return {
        ...oldData_2,
        pages: newPages_0
      };
    });
  };
  const deleteDraftPost = async (id_1: string, discardPost: (value: boolean) => void) => {
    const [error_3] = await client.posts.delete.mutate({
      postId: id_1
    });
    if (error_3) return;
    discardPost(true);
  };
  const editPost = async (id_2: string, content: string, isPrivate?: boolean, uploads?: {
    uri: string;
  }[], serverUploads?: {
    id: string;
  }[] | null, title?: string | null): Promise<boolean> => {
    let responseContent = content;
    let responseTitle: string | null | undefined;
    // A device-local post holds its whole body in `content`, so it is one row.
    let responseRows = 1;
    if (isPrivate) {
      const success_0 = await editPrivatePost(id_2, content, uploads);
      if (!success_0) {
        toast('Failed to edit private post', '', 'error');
        return false;
      }
    } else {
      const [error_4, response] = await client.posts.edit.mutate({
        postId: id_2,
        title,
        content,
        uploads: serverUploads
      });
      if (error_4) {
        toast('Failed to edit post', '', 'error');
        return false;
      }
      responseContent = response.content;
      responseTitle = response.title;
      responseRows = response.contentRows;
    }
    // Removed, not invalidated: invalidation only refetches enabled observers,
    // and every feed card holds a disabled one that would keep old rows alive.
    queryClient.removeQueries({
      queryKey: postBodyQueryKey(id_2)
    });
    const updatePostContent = (posts_0: Post[], postId_3: string, newContent: string): Post[] => {
      return posts_0.map(post_4 => {
        if (post_4.id === postId_3) {
          return {
            ...post_4,
            content: newContent,
            contentRows: responseRows,
            ...(responseTitle !== undefined ? {
              title: responseTitle
            } : {}),
            edited: true
          };
        }
        if (post_4.replies && post_4.replies.length > 0) {
          return {
            ...post_4,
            replies: updatePostContent(post_4.replies, postId_3, newContent)
          };
        }
        return post_4;
      });
    };
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      predicate: query => Array.isArray(query.queryKey) && query.queryKey[0] === 'Posts'
    }, data_0 => {
      if (!data_0) return data_0;
      const updatedPages_0 = data_0.pages.map(page_4 => ({
        ...page_4,
        posts: updatePostContent(page_4.posts, id_2, responseContent)
      }));
      return {
        ...data_0,
        pages: updatedPages_0
      };
    });
    return true;
  };
  const addOpenAiReply = async (id_3: string, reply_1: Post) => {
    const allPages = queryClient.getQueryData<InfiniteData<PostResponse>>(queryKey);
    if (!allPages) return false;
    const updatedPages_1 = allPages.pages.map(page_5 => ({
      ...page_5,
      posts: page_5.posts.map(post_5 => {
        if (post_5.id === id_3) {
          return {
            ...post_5,
            openaiReply: reply_1
          };
        }
        return post_5;
      })
    }));
    queryClient.setQueryData<InfiniteData<PostResponse>>(queryKey, {
      ...allPages,
      pages: updatedPages_1
    });
  };
  const appendPost = (post_6: Post) => {
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_3 => {
      if (!oldData_3) return oldData_3;
      const updatedPages_2 = [{
        ...oldData_3.pages[0],
        posts: [post_6, ...oldData_3.pages[0].posts]
      }, ...oldData_3.pages.slice(1)];
      return {
        ...oldData_3,
        pages: updatedPages_2
      };
    });
  };
  const postNewReplyHandler = async (parentPostId: string, content_0: string, anonymousPost: boolean, uploads_0: {
    uri: string;
    fileName?: string;
  }[], isPrivatePost: boolean) => {
    if (isPrivatePost) {
      // Private: pass local URIs directly to blob storage
      const createdReply = await createPrivatePost({
        content: content_0,
        uploads: uploads_0,
        factiiis: [],
        anonymous: anonymousPost,
        spaceId: undefined,
        parentPostId: parentPostId
      });
      if (createdReply) {
        await appendReply(createdReply);
      }
      return;
    }
    const serverUploads_0: Upload[] = [];
    for (const item of uploads_0) {
      const fileName = item.fileName || 'file';
      const dims = await getDimensionsFromUri(item.uri);
      const dimensions = dims ?? undefined;
      const result = await uploadOne(item.uri, fileName, dimensions, {
        forPost: true
      });
      if (!result) continue;
      serverUploads_0.push(result);
    }
    const [error_5, data_1] = await client.posts.new.mutate({
      content: content_0,
      parentPostId,
      uploads: serverUploads_0,
      anonymousPost,
      addFactiiis: []
    });
    if (error_5) return error_5;
    await appendReply(data_1.post);
  };
  const updateReplies = async (repliesCount: number, postId_4: string) => {
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_4 => {
      if (!oldData_4) return oldData_4;
      const updatedPages_3 = oldData_4.pages.map(page_6 => ({
        ...page_6,
        posts: page_6.posts.map(post_7 => {
          if (post_7.id === postId_4) {
            return {
              ...post_7,
              replies_count: repliesCount
            };
          }
          return post_7;
        })
      }));
      return {
        ...oldData_4,
        pages: updatedPages_3
      };
    });
  };
  const updateVote = async (voteCount: number, postId_5: string, voted?: VoteType | null) => {
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_5 => {
      if (!oldData_5) return oldData_5;
      const updatePostVotes = (posts_1: Post[]): Post[] => {
        return posts_1.map(post_8 => {
          if (post_8.id === postId_5) {
            const updatedPost = voted === undefined ? {
              ...post_8,
              voteCount
            } : {
              ...post_8,
              voteCount,
              voted
            };
            return updatedPost;
          } else if (post_8.replies && post_8.replies.length > 0) {
            return {
              ...post_8,
              replies: updatePostVotes(post_8.replies)
            };
          } else if (post_8.repost?.id === postId_5) {
            const updatedPost_0 = voted === undefined ? {
              ...post_8,
              repost: {
                ...post_8.repost,
                voteCount
              }
            } : {
              ...post_8,
              repost: {
                ...post_8.repost,
                voteCount,
                voted
              }
            };
            return updatedPost_0;
          }
          return post_8;
        });
      };
      const updatedPages_4 = oldData_5.pages.map(page_7 => ({
        ...page_7,
        posts: updatePostVotes(page_7.posts)
      }));
      return {
        ...oldData_5,
        pages: updatedPages_4
      };
    });
  };
  const refreshPost = async (id_4: string, isPrivatePost_0?: boolean) => {
    const replacePostRecursive = (posts_2: Post[], updated: Post): Post[] => {
      return posts_2.map(p => {
        if (p.id === id_4) return {
          ...updated,
          replies: p.replies
        };
        if (p.replies?.length) {
          return {
            ...p,
            replies: replacePostRecursive(p.replies, updated)
          };
        }
        return p;
      });
    };
    const updateCache = (updated_0: Post) => {
      queryClient.setQueriesData<InfiniteData<PostResponse>>({
        queryKey
      }, oldData_6 => {
        if (!oldData_6) return oldData_6;
        const updatedPages_5 = oldData_6.pages.map(page_8 => ({
          ...page_8,
          posts: replacePostRecursive(page_8.posts, updated_0)
        }));
        return {
          ...oldData_6,
          pages: updatedPages_5
        };
      });
    };
    try {
      if (isPrivatePost_0) {
        const post_9 = await fetchPrivatePost(id_4);
        if (post_9) {
          updateCache(post_9);
        }
      } else {
        const [error_7, data_2] = await client.posts.getMultiple.query({
          postIds: [id_4]
        });
        if (error_7) return false;
        const post_10 = data_2.posts[0];
        if (post_10?.id === id_4) {
          updateCache(post_10);
        }
      }
    } catch (error_6) {
      toast((error_6 as Error)?.message ?? 'An unknown error occurred', '', 'error');
      return false;
    }
  };
  const savePostHandler = async (id_5: string) => {
    const [error_8, result_0] = await client.posts.save.mutate({
      postId: id_5
    });
    if (error_8) return error_8;
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_7 => {
      if (!oldData_7) return oldData_7;
      const updatedPages_6 = oldData_7.pages.map(page_9 => ({
        ...page_9,
        posts: page_9.posts.map(post_11 => post_11.id === id_5 ? {
          ...post_11,
          saved: result_0.saved
        } : post_11)
      }));
      return {
        ...oldData_7,
        pages: updatedPages_6
      };
    });
  };
  const pinPostHandler = async (type: 'profile' | 'space', postId_6: string, spaceSlug?: string) => {
    let result_1;
    if (type === 'profile') {
      result_1 = await client.users.pinPost.mutate({
        postId: postId_6
      });
    } else {
      result_1 = await client.spaces.pinPost.mutate({
        postId: postId_6,
        slug: spaceSlug ?? ''
      });
    }
    if (result_1[0]) return;
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_8 => {
      if (!oldData_8) return oldData_8;
      let postToPin = null;
      let postFoundAndPinned = false;
      const updatedPages_7 = oldData_8.pages.map(page_10 => {
        const updatedPosts = page_10.posts.map(post_12 => {
          if (post_12.id === postId_6) {
            postToPin = {
              ...post_12,
              pinned: true
            };
            postFoundAndPinned = true;
            return {
              ...post_12,
              pinned: false
            };
          } else if (post_12.pinned) {
            return {
              ...post_12,
              pinned: false
            };
          }
          return post_12;
        }).filter(post_13 => post_13.id !== postId_6);
        return {
          ...page_10,
          posts: updatedPosts
        };
      });
      if (postToPin && postFoundAndPinned) {
        updatedPages_7[0].posts.unshift(postToPin);
      }
      return {
        ...oldData_8,
        pages: updatedPages_7
      };
    });
  };
  const unpinPostHandler = async (type_0: 'profile' | 'space', spaceSlug_0?: string) => {
    let result_2;
    if (type_0 === 'profile') {
      result_2 = await client.users.unpinPost.mutate();
    } else {
      if (!spaceSlug_0) return;
      result_2 = await client.spaces.unpinPost.mutate({
        slug: spaceSlug_0
      });
    }
    if (result_2[0]) return;
    queryClient.setQueriesData<InfiniteData<PostResponse>>({
      queryKey
    }, oldData_9 => {
      if (!oldData_9) return oldData_9;
      const updatedPages_8 = oldData_9.pages.map(page_11 => ({
        ...page_11,
        posts: page_11.posts.map(post_14 => ({
          ...post_14,
          pinned: false
        }))
      }));
      return {
        ...oldData_9,
        pages: updatedPages_8
      };
    });
  };
  const voteHandler = async (type_1: VoteType, id_6: string, isPrivatePost_1?: boolean, onAuthRequired?: () => void) => {
    if (!currentUser.isAuthenticated) {
      onAuthRequired?.();
      return;
    }
    if (isPrivatePost_1) {
      toast('Private posts cannot be voted on');
      return;
    }
    const [error_9, data_3] = await client.posts.vote.mutate({
      postId: id_6,
      type: type_1
    });
    if (error_9 || !data_3) return;
    await updateVote(data_3.totalVoteCount, data_3.postId, data_3.voted === 'UPVOTE' ? VoteType.UPVOTE : data_3.voted === 'DOWNVOTE' ? VoteType.DOWNVOTE : null);
  };
  const copyLinkHandler = async (post_15: Post) => {
    await copyToClipboard(`${SITE_URL}/post/${post_15.id}`);
    toast('Link copied', 'Link to post copied to your clipboard');
    if (linkCopiedStates[post_15.id]) return;
    setLinkCopiedStates(prev => ({
      ...prev,
      [post_15.id]: true
    }));
    setTimeout(() => {
      setLinkCopiedStates(prev_0 => ({
        ...prev_0,
        [post_15.id]: false
      }));
    }, 2000);
    setOpenedModal(undefined);
  };
  const openRepostScreenHandler = (post_16: Post) => {
    if (post_16.repost?.id) {
      toast('You cannot repost a repost.', '', 'error');
      return;
    }
    setOpenedModal('REPOST');
  };
  const deleteHandler = async (post_17: Post) => {
    const error_10 = await deletePost(post_17.id, post_17.isPrivate && !post_17.isServerPrivate);
    if (error_10) return setOpenedModal(undefined);
  };
  const openReportScreenHandler = () => {
    setOpenedModal('REPORT');
  };
  const awardButtonClickHandler = (post_18: Post, onAuthRequired_0?: () => void, onError?: (message: string) => void) => {
    if (!currentUser.isAuthenticated) {
      return onAuthRequired_0?.();
    }
    if (currentUser.username === post_18.user.username) {
      return onError?.('You cannot award a post you created.');
    }
    setOpenedModal('AWARD');
  };
  const reportClickHandler = (type_2: 'post' | 'user', post_19: Post, onAuthRequired_1?: () => void, onReport?: (reportType: ModerationReportType, id: string) => void, onClose?: () => void) => {
    if (!currentUser.isAuthenticated) {
      return onAuthRequired_1?.();
    }
    if (onReport) {
      // Client: calls AppWidgetContext.newModerationReport
      onReport(type_2 === 'post' ? ModerationReportType.POST : ModerationReportType.USER, type_2 === 'post' ? post_19.id : post_19.user.username);
    } else {
      // Mobile: just sets modal
      setOpenedModal('REPORT');
    }
    onClose?.();
  };
  const blockButtonClickHandler = async (blocked: boolean, setBlocked: (state: boolean) => void, username: string) => {
    if (blocked) {
      const [error_11] = await client.users.unblockUser.mutate({
        username
      });
      if (error_11) return error_11;
      toast(`@${username} unblocked successfully`);
      setBlocked(false);
    } else {
      const [error_12] = await client.users.blockUser.mutate({
        username
      });
      if (error_12) return error_12;
      toast(`@${username} blocked successfully`);
      setBlocked(true);
    }
  };
  const muteButtonClickHandler = async (muted: boolean, setMuted: (state: boolean) => void, username_0: string) => {
    if (muted) {
      const [error_13] = await client.users.unmuteUser.mutate({
        username: username_0
      });
      if (error_13) return error_13;
      setMuted(false);
    } else {
      const [error_14] = await client.users.muteUser.mutate({
        username: username_0
      });
      if (error_14) return error_14;
      setMuted(true);
    }
  };
  const banSpaceMember = async (spaceSlug_1: string | undefined, username_1: string, ruleId?: number) => {
    if (!spaceSlug_1) return;
    const [error_15] = await client.spaces.banUser.mutate({
      slug: spaceSlug_1,
      username: username_1,
      ruleId
    });
    if (!error_15) toast(`${username_1} banned successfully`);
    setOpenedModal(undefined);
  };
  const showBanConfirmation = (post_20: Post) => {
    setSelectedPostForBan(post_20);
    setOpenedModal('BAN_CONFIRMATION');
  };
  const confirmBanUser = async (ruleId_0?: number) => {
    if (!selectedPostForBan) return;
    await banSpaceMember(selectedPostForBan.space ?? undefined, selectedPostForBan.user.username, ruleId_0);
    setOpenedModal(undefined);
    setSelectedPostForBan(null);
  };
  const closeBanModal = () => {
    setOpenedModal(undefined);
    setSelectedPostForBan(null);
  };

  // Factiii classification helpers
  const isNsfw = (factiiis: PostFactiii[]) => {
    if (factiiis.length === 0) return false;
    return Boolean(factiiis.find(factiii => factiii.name.toLowerCase() === 'nsfw'.toLowerCase()));
  };
  const isPolitics = (factiiis_0: PostFactiii[], space: string | undefined | null) => {
    if (factiiis_0.length === 0) return false;
    return Boolean(factiiis_0.find(factiii_0 => factiii_0.name.toLowerCase() === 'politics'.toLowerCase())) || space === 'politics';
  };
  const isTroll = (factiiis_1: PostFactiii[]) => {
    if (factiiis_1.length === 0) return false;
    return Boolean(factiiis_1.find(factiii_1 => factiii_1.name.toLowerCase() === 'troll'.toLowerCase()));
  };
  return {
    // Query management
    queryKey,
    fetchPostReplies,
    // Post CRUD operations
    appendReply,
    postNewReplyHandler,
    deletePost,
    deleteDraftPost,
    setArchivedHandler,
    editPost,
    addOpenAiReply,
    appendPost,
    hidePost,
    // Reply and vote management
    updateReplies,
    updateVote,
    voteHandler,
    refreshPost,
    // Save and pin operations
    savePostHandler,
    pinPostHandler,
    unpinPostHandler,
    // Factiii operations
    postDataFactiii,
    // UI handlers
    copyLinkHandler,
    openRepostScreenHandler,
    openReportScreenHandler,
    awardButtonClickHandler,
    reportClickHandler,
    deleteHandler,
    // User moderation
    blockButtonClickHandler,
    muteButtonClickHandler,
    banSpaceMember,
    showBanConfirmation,
    confirmBanUser,
    closeBanModal,
    // Modal state
    openedModal,
    setOpenedModal,
    selectedPostForBan,
    linkCopiedStates,
    // Factiii helpers
    isNsfw,
    isPolitics,
    isTroll,
    // Private post operations (from useDatabase)
    createPrivatePost,
    deletePrivatePost,
    fetchPrivatePost,
    editPrivatePost,
    fetchPrivateMyFactiiiTags,
    fetchPrivatePosts,
    fetchPrivatePostReplies,
    // Current user reference
    currentUser,
    routeName
  };
};
export default usePosts;